import React, { useMemo, useState } from "react";
import { Box, Button, Popover, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import type { DepartmentUnavailability } from "../departmentAvailability";
import { calcularPosicionRangoPx } from "../../reservations/styles/reservationStyles";
import { formatDateOnly } from "../../shared/utils/dateUtils";
import { calendarPopoverPaperSx } from "../utils/calendarPopoverStyles";

interface Props {
  block: DepartmentUnavailability;
  department: string;
  days: Date[];
  dayWidth: number;
  canManage: boolean;
  onEdit?: (block: DepartmentUnavailability) => void;
  onDelete?: (block: DepartmentUnavailability) => void;
}

/** Ancho mínimo para que "Fuera de servicio" entre sin cortarse junto al ícono. */
const MIN_WIDTH_FOR_LABEL = 110;

/**
 * Banda de un departamento fuera de servicio. Misma geometría que una reserva
 * (empieza y termina a mitad de celda), pero deliberadamente sin color de
 * reserva ni de limpieza: gris pizarra rayado, el lenguaje universal de
 * "en mantenimiento".
 */
const DepartmentAvailabilityBlock: React.FC<Props> = ({
  block,
  department,
  days,
  dayWidth,
  canManage,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const color = theme.palette.app.availability.outOfService;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const position = useMemo(
    () => calcularPosicionRangoPx(block.desde, block.disponible_nuevamente, days, dayWidth, 0),
    [block.desde, block.disponible_nuevamente, dayWidth, days]
  );

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Departamento ${department} fuera de servicio: ${block.motivo}`}
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.currentTarget.click();
          }
        }}
        sx={{
          position: "absolute",
          left: position.leftPx,
          width: position.widthPx,
          top: 3,
          bottom: 3,
          pointerEvents: "auto",
          cursor: "pointer",
          border: "1px solid",
          borderColor: color,
          borderRadius: 1,
          bgcolor: alpha(color, 0.12),
          backgroundImage: `repeating-linear-gradient(135deg, transparent 0, transparent 7px, ${alpha(color, 0.22)} 7px, ${alpha(color, 0.22)} 11px)`,
          color: "text.primary",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          px: 0.5,
          overflow: "hidden",
          "&:focus-visible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 1,
          },
        }}
      >
        <EventBusyIcon sx={{ fontSize: 17, flexShrink: 0, color }} />
        {position.widthPx >= MIN_WIDTH_FOR_LABEL && (
          <Typography variant="caption" noWrap fontWeight={700}>
            Fuera de servicio
          </Typography>
        )}
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "center", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{ sx: calendarPopoverPaperSx(theme) }}
      >
        <Box sx={{ display: "grid", gap: 0.35 }}>
          <Typography variant="body2" fontWeight={700}>
            Depto {department} · Fuera de servicio
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Desde el {formatDateOnly(block.desde)} · disponible el {formatDateOnly(block.disponible_nuevamente)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>
            {block.motivo}
          </Typography>
          {canManage && (
            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", mt: 0.75 }}>
              <Button
                size="small"
                startIcon={<EditIcon fontSize="small" />}
                onClick={() => {
                  setAnchor(null);
                  onEdit?.(block);
                }}
              >
                Editar
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon fontSize="small" />}
                onClick={() => {
                  setAnchor(null);
                  onDelete?.(block);
                }}
              >
                Quitar
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default DepartmentAvailabilityBlock;
